import { Dispatch, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import { OrderSideEnum } from '../../../../lib/enums';
import { OrderStatusEnum } from '../../../../lib/enums/order-status.enum';
import lang from '../../../../lib/language';
import { Order } from '../../../../lib/models/trading/types';
import { getSummary } from '../../../../lib/store/ams/index';
import { getAccountCloseoutStatus } from '../../../../lib/store/closeout';
import {
  getBuyingPowerFromOMS as getBuyingPowerFromOMSCall,
} from '../../../../lib/store/common-actions';
import { getCurrentAccount } from '../../../../lib/store/crm/helpers';
import { RootState } from '../../../../lib/store/index';
import {
  getAccountTradeHistory,
  getOpenPositions as getPositions,
  setHasOrderUpdate,
} from '../../../../lib/store/reporting';
import { PENDING_ORDERS_REQUEST_TENNSTATUS } from '../../../../lib/store/reporting/constants';
import {
  AccountTradeHistoryPayloadData,
  OpenPosition,
  PositionType,
} from '../../../../lib/store/reporting/types';
import {
  newOrder as serviceNewOrder,
  resetRequestStatus,
} from '../../../../lib/store/trading/index';
import MarketStateCache from '../../../../lib/store-util/MarketStateCache';
import SubscribeGroupCache from '../../../../lib/store-util/SubscribeGroupCache';
import SymbolStatusCache from '../../../../lib/store-util/SymbolStatusCache';
import { checkIfSymbolIsHalted } from '../../../../lib/util/HaltSymbolHelper';
import { isMarketClosed } from '../../../../lib/util/MarketDataHelpers';
import { hasInsufficientPositionQuantity, shouldFetchCloseoutStatus } from '../../../../lib/util/trading-position-helpers';
import { isMessageSummaryAffectingStatus } from '../../../../lib/util/trading-status-helpers';
import {
  orderStatusToCallStatus,
} from '../../../../lib/util/TradingHelpers';

import ArrowBuyOrSell from '../../../../assets/img/arrowBuyOrSell.png';
import BuyAndSellDot from '../../../../assets/img/BuyAndSellDot.png';
import ModalCreateOrder from '../../../../containers/SymbolDetails/modalCreateOrder/ModalCreateOrder';
import { useIsMobile, useMaintenanceMode, useRowActionsMenu } from '../../../../hooks';
import Button from '../../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../../shared_components/Button/Button.types';
import ClickableImage from '../../../../shared_components/ClickableImage/ClickableImage';
import { UI_ROUTES } from '../../../../util/routes';
import { ClickableDiv, ProcessFinishedModal } from '../../..';
import HaltedSymbol from '../../../OrderActions/HaltedSymbol/HaltedSymbol';
import {
  buildOrderResultMessages,
} from '../../../OrderActions/OrderHelpers';
import useNewOrderTimer from '../../../OrderActions/useNewOrderTimer';
import useRefreshBuyingPower from '../../../OrderActions/useRefreshBuyingPower';

import styles from './ButtonsSellAndBuy.module.scss';

type Props = {
  isSwipeable?: boolean;
  row: any;
  showButtons?: boolean;
  setIsSellOrBuySelected?: (value: boolean) => void;
} & ReduxProps;

const ButtonsSellAndBuy = (props: Props) => {
  const {
    requestStatus,
    row,
    isSwipeable,
    submittedOrder,
    openPositions,
    accountReferenceId,
    getOpenPositions,
    getOrders,
    myStocksUpdate,
    newOrder,
    resetStatus,
    getMessageSummary,
    showButtons = true,
    getBuyingPowerFromOMS,
    getCloseoutStatus,
    setIsSellOrBuySelected,
  } = props;
  const { isMobile } = useIsMobile();
  const isMaintenance = useMaintenanceMode();
  const { isOpen: isMenuOpen, menuStyle, openMenu, closeMenu } = useRowActionsMenu();
  const navigate = useNavigate();
  const [isSell, setIsSell] = useState<boolean>(false);
  const [order, setOrder] = useState<Order>({});
  const [fromReview, setFromReview] = useState<boolean>(false);
  const [isHalted, setIsHalted] = useState<boolean>(false);
  const [positionsQuantity, setPositionsQuantity] = useState<number>(0);
  const [showFinishModal, setShowFinishModal] = useState<boolean>(false);
  const [notEnoughStocks, setNotEnoughStocks] = useState<boolean>(false);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState<boolean>(false);
  const { marketState } = MarketStateCache.use();
  const { name, id, positionType, quantity } = row;
  const symbolsStatusCache = SymbolStatusCache.use(name);

  const { ordType, orderQty, side, status } = submittedOrder;
  const rejectedOrder = requestStatus === OrderStatusEnum.Rejected;
  const orderInProgress = requestStatus === OrderStatusEnum.InProgress;

  const { orderTimeoutPassed, showExpired, showFillOrder, hasNoResponse } = useNewOrderTimer(
    showFinishModal,
    ordType,
    status,
    requestStatus,
  );

  const marketClosed = isMarketClosed(marketState);

  useEffect(() => {
    if (fromReview && requestStatus !== OrderStatusEnum.Pending && !showFinishModal) {
      if (
        !marketClosed
        && hasInsufficientPositionQuantity(
          { positionType, quantity },
          { side: isSell ? OrderSideEnum.Sell : OrderSideEnum.Buy, orderQty: order.orderQty },
        )
      ) {
        setNotEnoughStocks(true);
      } else {
        newOrder(order);
        setShowFinishModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromReview, requestStatus]);

  useEffect(() => {
    if (showFinishModal) {
      setShowCreateOrderModal(false);
      setFromReview(false);
    }
  }, [showFinishModal]);

  const handleBuySellClick = (isSellClicked: boolean) => {
    if (checkIfSymbolIsHalted(symbolsStatusCache)) {
      setIsHalted(true);
      return;
    }

    setIsSell(isSellClicked);
    setShowFinishModal(false);

    if (
      (!isSellClicked && positionType === PositionType.Sell)
      || (isSellClicked && positionType === PositionType.Buy)
    ) {
      setPositionsQuantity(quantity);
    } else {
      setPositionsQuantity(0);
    }

    if (isMobile) {
      const target = isSellClicked ? PositionType.Sell : PositionType.Buy;
      navigate(
        {
          pathname: `/${target}/${id}`,
        },
        {
          state: { stateSymbol: id, stateTarget: target, origin: UI_ROUTES.dashboard },
          replace: true,
        },
      );
    } else {
      setShowCreateOrderModal(!showCreateOrderModal);
    }
  };

  const onCloseResultOrderModal = () => {
    if (orderInProgress) {
      resetStatus();
    }
    if (!marketClosed) {
      getOpenPositions();
      myStocksUpdate(false);

      if (shouldFetchCloseoutStatus(openPositions, { symbol: name, side, orderQty, status }, accountReferenceId)) {
        getCloseoutStatus(accountReferenceId);
      }

      if (isMessageSummaryAffectingStatus(status)) {
        getMessageSummary();
      }
    }
    SubscribeGroupCache.setComponent('initial');
    getOrders({
      tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS,
      isPendingOrdersCall: true,
    });
  };

  const handleCloseOrder = (value) => {
    SubscribeGroupCache.setComponent('initial');
    setShowCreateOrderModal(value);
  };

  const handleRenderModalCreateOrder = () => (
    <ModalCreateOrder
      isMyAccount
      symbol={name}
      orderSide={isSell ? OrderSideEnum.Sell : OrderSideEnum.Buy}
      hasMyStocksOrderUpdate
      positionsQuantity={positionsQuantity}
      isOpenModalCreateOrder={showCreateOrderModal}
      setOrderInfo={(value) => setOrder(value)}
      handlePopUp={handleCloseOrder}
      setFromReview={(value) => setFromReview(value)}
    />
  );

  const handleCloseSelect = () => {
    closeMenu();
    setIsSellOrBuySelected && setIsSellOrBuySelected(false);
  };

  const handleActionClick = (isSellClicked: boolean) => {
    handleBuySellClick(isSellClicked);
    handleCloseSelect();
  };

  const showResultModal = (orderTimeoutPassed || hasNoResponse || rejectedOrder) && showFinishModal;

  useRefreshBuyingPower(showResultModal, getBuyingPowerFromOMS);

  return (
    <>
      {!isMaintenance && (isMobile ? (
        <div className={isSwipeable ? styles.wrapperButtonsSwipable : styles.wrapperButtonsMobile}>
          <Button
            className={isSwipeable ? styles.btnSellMobileSwipable : styles.btnSellMobile}
            variant={ButtonTypesEnum.Link}
            onClick={() => handleBuySellClick(true)}
          >
            {lang.commonOrderSideButtonSell()}
          </Button>
          <Button
            className={isSwipeable ? styles.btnBuyMobileSwipable : styles.btnBuyMobile}
            variant={ButtonTypesEnum.Link}
            onClick={() => handleBuySellClick(false)}
          >
            {lang.commonOrderSideButtonBuy()}
          </Button>
        </div>
      ) : (
        <ClickableDiv className={styles.dotsWrapper} onClick={(e) => e.stopPropagation()}>
          <ClickableImage
            src={BuyAndSellDot}
            className={styles.dotSeparatedIcon}
            onClick={openMenu}
            alt="img"
          />
          {(showButtons || isMenuOpen) && (
            <div
              className={styles.selectWrapper}
              onMouseEnter={() => setIsSellOrBuySelected && setIsSellOrBuySelected(true)}
              onMouseLeave={() => setIsSellOrBuySelected && setIsSellOrBuySelected(false)}
            >
              {isMenuOpen && menuStyle && createPortal(
                <>
                  <ClickableDiv className={styles.selectBackdrop} onClick={handleCloseSelect} />
                  <div className={styles.selectMenu} style={menuStyle}>
                    <ClickableDiv
                      className={styles.actionButton}
                      onClick={() => handleActionClick(false)}
                    >
                      <img src={ArrowBuyOrSell} alt="buy" />
                      <span className={styles.text}>{lang.commonOrderSideButtonBuy()}</span>
                    </ClickableDiv>
                    <ClickableDiv
                      className={styles.actionButton}
                      onClick={() => handleActionClick(true)}
                    >
                      <img src={ArrowBuyOrSell} className={styles.image} alt="sell" />
                      <span className={styles.text}>{lang.commonOrderSideButtonSell()}</span>
                    </ClickableDiv>
                  </div>
                </>,
                document.body,
              )}
            </div>
          )}
        </ClickableDiv>
      ))}
      {isHalted && (
        <HaltedSymbol symbolName={row.companyName ?? name} toggleModal={() => setIsHalted(false)} />
      )}
      {handleRenderModalCreateOrder()}
      {showResultModal && (
        <ProcessFinishedModal
          symbol={name}
          {...buildOrderResultMessages(name, submittedOrder, requestStatus, { showExpired, showFillOrder })}
          toggleModal={onCloseResultOrderModal}
        />
      )}
      {notEnoughStocks && (
        <ProcessFinishedModal
          symbol={name}
          processSuccessMessageTitle=""
          callStatus={orderStatusToCallStatus(OrderStatusEnum.Rejected)}
          processFailedMessage={lang.webTradingIncorrectStocks()}
          toggleModal={() => {
            setNotEnoughStocks(false);
            myStocksUpdate(false);
          }}
        />
      )}
    </>
  );
};

const mapStateProps = (state: RootState) => ({
  requestStatus: state.trading.requestStatus,
  submittedOrder: state.trading.order,
  openPositions: state.reporting.openPositions as OpenPosition[],
  accountReferenceId: getCurrentAccount(state),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  resetStatus: () => dispatch(resetRequestStatus()),
  newOrder: (order: Order) => dispatch(serviceNewOrder({ order })),
  getOrders: (data: AccountTradeHistoryPayloadData) => dispatch(getAccountTradeHistory(data)),
  getOpenPositions: (symbol?: string) => dispatch(getPositions({ symbol })),
  myStocksUpdate: (hasUpdate: boolean) => dispatch(setHasOrderUpdate(hasUpdate)),
  getMessageSummary: () => dispatch(getSummary()),
  getBuyingPowerFromOMS: () => dispatch(getBuyingPowerFromOMSCall()),
  getCloseoutStatus: (accountRefId: string) => dispatch(getAccountCloseoutStatus(accountRefId)),
});

const connector = connect(mapStateProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ButtonsSellAndBuy);
